import { useEffect } from 'react'
import { Button, Group, Modal, Stack, Switch, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { areaEntrada, type Area, type AreaEntrada } from '@serkat/shared'
import { ErrorApi, api } from '../../../api/cliente'

interface Props {
  abierto: boolean
  onCerrar: () => void
  /** Presente al editar desde el módulo; ausente al dar de alta. */
  inicial?: Area | undefined
  /** Lo que el usuario ya escribió buscando el área que no encontró. */
  nombreSugerido?: string | undefined
  /** Recibe el área guardada, para que quien la pidió pueda seleccionarla. */
  onGuardada?: ((a: Area) => void) | undefined
}

const VACIO: AreaEntrada = { codigo: '', nombre: '', activo: true }

/**
 * Alta y edición de áreas. Gemelo de `ModalObra`: lo usan el módulo de Áreas y
 * el formulario de órdenes, cuando el área no está en la lista y no tiene
 * sentido abandonar la orden a medias para ir a crearla.
 */
export function ModalArea({ abierto, onCerrar, inicial, nombreSugerido, onGuardada }: Props) {
  const qc = useQueryClient()

  const form = useForm<AreaEntrada>({
    initialValues: VACIO,
    validate: zodResolver(areaEntrada),
  })

  // Se recarga al abrir, no al montar: el modal permanece montado entre
  // aperturas y sin esto la segunda alta arrastraría lo escrito en la primera.
  useEffect(() => {
    if (!abierto) return
    form.setValues(
      inicial
        ? {
            codigo: inicial.codigo, nombre: inicial.nombre, activo: inicial.activo,
          }
        : { ...VACIO, nombre: nombreSugerido ?? '' },
    )
    form.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, inicial, nombreSugerido])

  const guardar = useMutation({
    mutationFn: (v: AreaEntrada) =>
      inicial ? api.patch<Area>(`/areas/${inicial.id}`, v) : api.post<Area>('/areas', v),
    onSuccess: (a) => {
      notifications.show({ color: 'teal', message: 'Área guardada' })
      void qc.invalidateQueries({ queryKey: ['areas'] })
      // Los catálogos se cachean una hora. Sin invalidarlos, el área recién
      // creada no aparecería en el selector que la pidió.
      void qc.invalidateQueries({ queryKey: ['catalogos'] })
      onGuardada?.(a)
      onCerrar()
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  return (
    <Modal
      opened={abierto}
      onClose={onCerrar}
      title={inicial ? 'Editar área' : 'Nueva área'}
      // Por encima del formulario de la orden, que también es un modal.
      zIndex={300}
    >
      <form onSubmit={form.onSubmit((v) => guardar.mutate(v))}>
        <Stack gap="sm">
          <TextInput label="Nombre" withAsterisk data-autofocus {...form.getInputProps('nombre')} />
          <TextInput
            label="Código"
            withAsterisk
            description="El que usa el área. Se guarda en mayúsculas"
            placeholder="EQUIP_MED"
            {...form.getInputProps('codigo')}
          />
          {inicial && (
            <Switch
              label="Activa"
              description="Las áreas disueltas se desactivan: salen del selector y conservan su historial"
              {...form.getInputProps('activo', { type: 'checkbox' })}
            />
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onCerrar}>Cancelar</Button>
            <Button type="submit" loading={guardar.isPending}>Guardar</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
